import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import heroBarbers from "@/assets/hero-barbers.jpg";
import aboutVakmanschap from "@/assets/about-vakmanschap.jpg";
import barberBo from "@/assets/barber-bo.jpg";
import barberSyl from "@/assets/barber-syl.jpg";

import barberJesus from "@/assets/barber-jesus.jpg";
import barberFlorencia from "@/assets/barber-florencia.jpg";
const About = () => {
  const contentSection = useScrollAnimation();
  const statsSection = useScrollAnimation();
  const barbersSection = useScrollAnimation();
  return <div className="min-h-screen">
      <SEO
        title="Over Bo Barbers | The Barbers in Utrecht"
        description="Maak kennis met het team van Bo Barbers — vakmanschap, ervaring en passie voor herenkapsels en baardverzorging in hartje Utrecht."
        path="/about"
      />
      <Header />
      
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <img src={heroBarbers} alt="Bo Barbers Team" className="w-full h-full object-cover animate-cinematic-zoom" />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 py-24 sm:py-28 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/70 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold animate-fade-in" style={{
            animationDelay: "0.2s",
            animationFillMode: "forwards"
          }}>
              OVER ONS
            </p>
            <h1 style={{
            animationDelay: "0.4s",
            animationFillMode: "forwards"
          }} className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 text-white leading-[0.95] animate-fade-in px-4 xl:text-5xl">
              The Barbers
            </h1>
            <div className="w-16 sm:w-24 h-1 bg-white mx-auto mb-6 sm:mb-8 animate-fade-in" style={{
            animationDelay: "0.6s",
            animationFillMode: "forwards"
          }}></div>
            <p className="text-white/80 max-w-xl mx-auto text-sm sm:text-base leading-relaxed animate-fade-in px-4" style={{
            animationDelay: "0.8s",
            animationFillMode: "forwards"
          }}>
              Nobelstraat 263, 3512 EM Utrecht
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section ref={contentSection.ref} className="py-16 sm:py-20 md:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center mb-16 sm:mb-20 md:mb-24 transition-all duration-1000 ${contentSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div>
              <h2 className="font-display font-bold mb-4 sm:mb-6 leading-tight text-xl sm:text-2xl">
                ERVAAR VAKMANSCHAP, PRECISIE
              <br />
                <span className="text-primary text-xl sm:text-2xl">EN EEN BARBERSHOP MET KARAKTER</span>
              </h2>
              <p className="text-muted-foreground mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                Bo Barbers is een moderne barbershop waar klassieke barbering wordt gecombineerd met stijl, vakmanschap en persoonlijke aandacht. Een plek waar je terecht kunt voor een strak kapsel, een verzorgde fade of een perfect uitgevoerde scheerbeurt, altijd met oog voor detail.
              </p>
              <p className="text-muted-foreground mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                ​Bij Bo Barbers draait het om meer dan alleen het eindresultaat. De ervaring in de stoel, het advies en het vakmanschap vormen samen de kwaliteit waarvoor klanten terugkomen. Ons team bestaat uit barbiers met uiteenlopende achtergronden, opgebouwd in binnen- en buitenland. Die mix van stijlen, technieken en ervaring zorgt ervoor dat iedere klant een behandeling krijgt die precies past.
              </p>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                ​Wat ons onderscheidt? Een sterke focus op kwaliteit, een hecht team en een professionele aanpak. Iedere barber brengt zijn of haar eigen specialismen mee, waardoor we als team elke knip- en scheerbehandeling naar een hoger niveau tillen.
              </p>
            </div>
            <div className="overflow-hidden group order-first lg:order-last">
              <img alt="Bo Barbers Vakmanschap" className="rounded-lg shadow-2xl transition-transform duration-700 group-hover:scale-110" src={aboutVakmanschap} />
            </div>
          </div>

          {/* Stats */}
          <div ref={statsSection.ref} className={`grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-16 sm:mb-20 md:mb-24 transition-all duration-1000 ${statsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Card className="p-6 sm:p-8 bg-card border-border text-center transition-all duration-500 hover:shadow-lg hover:-translate-y-2" style={{
            transitionDelay: statsSection.isVisible ? '0ms' : '0ms'
          }}>
              <div className="text-4xl sm:text-5xl font-display font-bold text-primary mb-2">10+</div>
              <p className="text-muted-foreground uppercase tracking-wider text-xs sm:text-sm">Jaren Ervaring</p>
            </Card>
            <Card className="p-6 sm:p-8 bg-card border-border text-center transition-all duration-500 hover:shadow-lg hover:-translate-y-2" style={{
            transitionDelay: statsSection.isVisible ? '150ms' : '0ms'
          }}>
              <div className="text-4xl sm:text-5xl font-display font-bold text-primary mb-2">3.7K+</div>
              <p className="text-muted-foreground uppercase tracking-wider text-xs sm:text-sm">Tevreden Klanten</p>
            </Card>
            <Card className="p-6 sm:p-8 bg-card border-border text-center transition-all duration-500 hover:shadow-lg hover:-translate-y-2" style={{
            transitionDelay: statsSection.isVisible ? '300ms' : '0ms'
          }}>
              <div className="text-4xl sm:text-5xl font-display font-bold text-primary mb-2">200+</div>
              <p className="text-muted-foreground uppercase tracking-wider text-xs sm:text-sm">5-Sterren Reviews</p>
            </Card>
          </div>

          {/* The Barbers Section */}
          <div ref={barbersSection.ref} className="mb-16 sm:mb-20 md:mb-24">
            <h3 className={`font-display text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center transition-all duration-1000 ${barbersSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>The Barbers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {/* Bo */}
              <Card className={`p-4 sm:p-6 bg-card border-border transition-all duration-700 hover:shadow-lg hover:-translate-y-2 ${barbersSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: barbersSection.isVisible ? '0ms' : '0ms'
            }}>
                <div className="mb-4 overflow-hidden rounded-lg group">
                  <img src={barberBo} alt="Bo - Barber" className="w-full h-80 sm:h-64 object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <h4 className="font-semibold text-lg sm:text-xl mb-1">Bo</h4>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3">Barber</p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Als derde generatie uit een kappersfamilie heb ik het vak met de paplepel binnengekregen. Mijn reis bracht me langs LaVie Kappers, Studio 7 en Kings Barbers, en zelfs naar Barcelona, waar ik ervaring opdeed bij Elijah. In Bo Barbers heb ik al die invloeden samengebracht in één plek waar authenticiteit, techniek en stijl elkaar versterken. Mijn focus ligt op perfectie: van klassieke contouren tot moderne fades, altijd met aandacht voor detail en uitstraling.
                </p>
              </Card>

              {/* Syl */}
              <Card className={`p-4 sm:p-6 bg-card border-border transition-all duration-700 hover:shadow-lg hover:-translate-y-2 ${barbersSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: barbersSection.isVisible ? '150ms' : '0ms'
            }}>
                <div className="mb-4 overflow-hidden rounded-lg group">
                  <img src={barberSyl} alt="Syl - Barber" className="w-full h-80 sm:h-64 object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <h4 className="font-semibold text-lg sm:text-xl mb-1">Syl</h4>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3">Barber</p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  ​Opgegroeid in Wijk bij Duurstede ontdekte ik al vroeg mijn passie voor het barbiersvak. Na mijn opleiding aan Barberschool Amsterdam kon ik mijn skills aanscherpen in verschillende shops. Bij Bo Barbers combineer ik techniek met creativiteit en zoek ik altijd naar de snit die het best past bij de persoonlijkheid van de klant.
Wat mij het meest typeert: strakke fades, cleane lijnen en moderne stijlen die net dat beetje extra karakter geven.
                </p>
              </Card>


              {/* Jesus */}
              <Card className={`p-4 sm:p-6 bg-card border-border transition-all duration-700 hover:shadow-lg hover:-translate-y-2 ${barbersSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: barbersSection.isVisible ? '450ms' : '0ms'
            }}>
                <div className="mb-4 overflow-hidden rounded-lg group">
                  <img src={barberJesus} alt="Jesus - Barber" className="w-full h-80 sm:h-64 object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <h4 className="font-semibold text-lg sm:text-xl mb-1">Jesus</h4>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3">Barber</p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  After running my own barbershop for years, I was ready for a new challenge. My move to the Netherlands in 2022 brought me to Bo Barbers: a place where craftsmanship, team spirit and ambition come together. I combine classic barber techniques with a modern approach, so every client gets a timeless yet fresh look.
My signature style: clean, balanced and always tailored to the person in the chair.
                </p>
              </Card>

              {/* Florencia */}
              <Card className={`p-4 sm:p-6 bg-card border-border transition-all duration-700 hover:shadow-lg hover:-translate-y-2 ${barbersSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: barbersSection.isVisible ? '600ms' : '0ms'
            }}>
                <div className="mb-4 overflow-hidden rounded-lg group">
                  <img src={barberFlorencia} alt="Florencia - Barber" className="w-full h-80 sm:h-64 object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <h4 className="font-semibold text-lg sm:text-xl mb-1">Florencia</h4>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3">Barber</p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  With me, every appointment begins with a personalized consultation. I take the time to listen to what you want so we can create a plan that fits your hair and lifestyle. My goal is simple: you leave my chair feeling confident with a style that truly represents you.
                  {" "}I've learned from masters around the world: Cuban fading, Italian beard work, and European scissor techniques. Having curls myself, curls are a passion, and I also enjoy working with thicker, straighter textures. Where others struggle, I thrive.
                  {" "}Fun, warm, confident, and professional, my Latin American roots inspire the way I bring out each client's best version!
                </p>
              </Card>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>;
};
export default About;